import {
  Filtering,
  QBox,
  QMultiSelect,
  QSelectItem,
  QSelectPlaceholder,
  QText,
} from '@qualio/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QualioDocument } from '../../../../api/model/document';
import { Template } from '../../../../api/templateApi';
import { sortByLabel } from '../../../../util/SelectionOptionUtils';
import { WorkspaceFilteringDefinition } from '../../../../util/WorkspaceUtils';

type TemplatesFilterProps = {
  isLoadingTemplates: boolean;
  templates: Template[];
  setSelectedDocumentsMap?: (selected: Map<number, QualioDocument>) => void;
};

export const TemplatesFilter = ({
  isLoadingTemplates,
  templates,
  setSelectedDocumentsMap,
}: TemplatesFilterProps) => {
  const { t } = useTranslation();
  const filtering = Filtering.useFiltering<WorkspaceFilteringDefinition>();
  const templateParams = filtering.filters?.filteringTemplates?.value;
  const selectedTemplates = (templateParams ?? []).filter(
    (template): template is string => template !== null,
  );

  const templateOptions: QSelectItem<string, string>[] = useMemo(
    () =>
      templates
        .map((template) => ({
          label: `${template.name} (${template.prefix})`,
          value: template.prefix.toString(),
        }))
        .sort(sortByLabel),
    [templates],
  );

  const onTemplateFilterChange = (
    templateItem: readonly QSelectItem<string, string>[],
  ) => {
    const templateIds = templateItem.map((tagItem) => tagItem.value);
    filtering.setFilters({
      filteringTemplates: templateIds,
    });
    // reset selection in workspace
    if (setSelectedDocumentsMap) {
      setSelectedDocumentsMap(new Map());
    }
  };

  return (
    <QBox w="300px" data-cy="templates-filter">
      <QMultiSelect
        isLoading={isLoadingTemplates}
        size="sm"
        options={templateOptions}
        onChange={onTemplateFilterChange}
        value={selectedTemplates}
        aria-label={t('labels.documentTemplates')}
        inputId="templates-filter-input"
      >
        <QSelectPlaceholder>
          <QText fontSize="sm">{t('forms.filterByType')}</QText>
        </QSelectPlaceholder>
      </QMultiSelect>
    </QBox>
  );
};
